import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Input, Badge } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import { fetchLicenseTenants, fetchPendingApprovals } from '../../actions/cspLicensesActions'
import CSPLicenseSettingsModal from './CSPLicenseSettingsModal'
import CSPLicenseDevPanel from './CSPLicenseDevPanel'
import { useCSPLicenseEffectiveRoles } from './devImpersonation'
import { canUseLicenceFeature } from './featureAccess'
import { formatDateTime } from '../../Shared/formatDateTime'

const CSPLicensesInner = ({ realRoles }) => {
  const dispatch = useDispatch()
  const tenants = useSelector((s) => s.cspLicenses.tenants)
  const tenantsLoading = useSelector((s) => s.cspLicenses.tenantsLoading)
  const pendingApprovals = useSelector((s) => s.cspLicenses.pendingApprovals)

  // Effective roles honour the dev-impersonation override when active;
  // otherwise they're the AAD roles passed in. All UI role gates (tab
  // visibility, settings access) read from here so they stay in sync
  // with what the backend sees on the corresponding API calls.
  const roles = useCSPLicenseEffectiveRoles(realRoles)
  const isAdmin = roles.includes('Admin')
  const canApprove = isAdmin
    || roles.includes('ITManager')
    || roles.includes('SSE')
  // Activity Log is Admin + Sales only (per Sprint 4 plan); ITM/SSE
  // see their own slice via My Requests and the peer queue via
  // Approvals but not the cross-tenant audit feed.
  const canSeeActivityLog = isAdmin || roles.includes('Sales')

  const [searchText, setSearchText] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchLicenseTenants())
    if (canApprove) dispatch(fetchPendingApprovals())
  }, [dispatch, canApprove])

  const visibleTenants = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    const list = tenants || []
    if (!q) return list
    return list.filter((t) =>
      [t.tenantName, t.companyName, t.domainPrefix, t.tenantId]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    )
  }, [tenants, searchText])


  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-4">
          <Col>
            <PageHeading>365 Licenses</PageHeading>
            <small className="text-muted">
              Browse mapped tenants and manage their Microsoft 365 licences.
            </small>
          </Col>
          <Col xs="auto">
            <Link to="/CSPLicenses/MyRequests" className="btn btn-outline-secondary mr-2">
              My Requests
            </Link>
            {canApprove && (
              <Link to="/CSPLicenses/Approvals" className="btn btn-outline-secondary mr-2">
                Approvals
                {pendingApprovals && pendingApprovals.length > 0 && (
                  <Badge color="warning" pill className="ml-2">
                    {pendingApprovals.length}
                  </Badge>
                )}
              </Link>
            )}
            {canSeeActivityLog && (
              <Link to="/CSPLicenses/ActivityLog" className="btn btn-outline-secondary mr-2">
                Activity Log
              </Link>
            )}
            <Link to="/CSPLicenses/Products" className="btn btn-outline-secondary mr-2">
              Products
            </Link>
            {/*
              Admin only. Everything in here is an admin-owned control -
              approval thresholds, MPN ID, DD routing, alert thresholds - and
              the modal is read-only for everyone else, so showing it to other
              roles offers a button that can only disappoint. The cart's
              threshold warnings come from a separate fetch, not from here.
            */}
            {isAdmin && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSettingsOpen(true)}
              >
                Settings
              </button>
            )}
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Input
              type="text"
              placeholder="Search by company, tenant, or domain..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              bsSize="sm"
            />
          </Col>
          <Col xs="auto" className="ml-auto">
            <small className="text-muted">
              {visibleTenants.length} of {(tenants || []).length} tenants
            </small>
          </Col>
        </Row>

        {tenantsLoading ? (
          <LoadingSpinner />
        ) : visibleTenants.length === 0 ? (
          <div className="text-muted">
            {(tenants || []).length === 0
              ? 'No tenants are mapped yet. Map one from the CSP Renewals page first.'
              : 'No tenants match your search.'}
          </div>
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Company</th>
                <th>Domain</th>
                <th className="text-right">Active subscriptions</th>
                <th>Last synced</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleTenants.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.companyName || '—'}</strong>
                  </td>
                  <td>
                    {t.domainPrefix ? (
                      <code className="small">{t.domainPrefix}.onmicrosoft.com</code>
                    ) : '—'}
                  </td>
                  <td className="text-right">
                    <Badge color={t.activeSubscriptionCount > 0 ? 'primary' : 'secondary'}>
                      {t.activeSubscriptionCount}
                    </Badge>
                  </td>
                  <td><small className="text-muted">{formatDateTime(t.lastSyncedAt)}</small></td>
                  <td>
                    <Link
                      to={`/CSPLicenses/Tenant/${t.id}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Container>

      {/* Not just the button: the modal fetches settings on open, so leaving
          it mounted for non-admins would still issue the request. */}
      {isAdmin && (
        <CSPLicenseSettingsModal
          isOpen={settingsOpen}
          toggle={() => setSettingsOpen(false)}
          isAdmin={isAdmin}
        />
      )}
      <CSPLicenseDevPanel />
    </PageContainer>
  )
}

const CSPLicenses = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      // The outer gate uses REAL AAD roles - dev impersonation is
      // meant for switching among the roles you already have access to,
      // not for granting access. Anyone without any of the four roles
      // would have no menu link to /CSPLicenses anyway; this 403 page
      // is the direct-URL backstop.
      const realRoles = accountInfo?.account?.idToken?.roles || []
      const allowed = canUseLicenceFeature(realRoles)

      if (!allowed) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }
      return <CSPLicensesInner realRoles={realRoles} />
    }}
  </AzureAD>
)

export default CSPLicenses
